import { captureRemixErrorBoundaryError } from "@sentry/remix";
import stylesheet from "~/tailwind.css?url";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  isRouteErrorResponse,
} from "@remix-run/react";
import type {
  LinksFunction,
  MetaFunction,
  LoaderFunction,
  HeadersFunction,
} from "@vercel/remix";
import { rootAuthLoader } from "@clerk/remix/ssr.server";
import { ClerkApp } from "@clerk/remix";

export const meta: MetaFunction = () => [
  { charset: "utf-8" },
  {
    title: "Cloud Exam Hub | AWS Practice Exams",
  },
  { viewport: "width=device-width,initial-scale=1" },
];

export const links: LinksFunction = () => {
  return [
    {
      rel: "stylesheet",
      href: stylesheet,
    },
    {
      rel: "icon",
      type: "image/x-icon",
      href: "/favicon.ico",
    },
  ];
};

export const loader: LoaderFunction = (args) => rootAuthLoader(args);

export const headers: HeadersFunction = () => {
  return {
    "Content-Type": "text/html; charset=utf-8",
    "Content-Security-Policy": `
      default-src 'self' https://*.tidiochat.com https://*.clerk.accounts.dev https://accounts.dev;
      script-src 'self' 'unsafe-inline' https://*.clerk.accounts.dev https://js.stripe.com https://www.googletagmanager.com https://www.google-analytics.com https://*.posthog.com https://vercel.live https://*.cloudexamhub.com https://*.cloudflare.com http://*.tidio.co https://*.tidiochat.com;
      style-src 'self' 'unsafe-inline' https://fonts.googleapis.com;
      img-src 'self' data: https://*.clerk.com https://*.cloudflare.com https://*.tidiochat.com https://www.googletagmanager.com;
      font-src 'self' https://fonts.gstatic.com https://*.tidiochat.com;
      frame-src 'self' https://*.clerk.accounts.dev https://js.stripe.com https://checkout.stripe.com https://*.posthog.com https://www.google-analytics.com https://vercel.live https://*.cloudexamhub.com https://*.cloudflare.com https://*.tidio.co;
      connect-src 'self' https://*.sentry.io https://*.clerk.com https://*.clerk.accounts.dev https://api.stripe.com https://checkout.stripe.com https://*.posthog.com https://www.google-analytics.com https://vercel.live https://*.cloudexamhub.com https://*.cloudflare.com https://*.tidiochat.com wss://*.tidio.co https://*.tidio.co https://clerk-telemetry.com;
      worker-src 'self' blob:;
    `
      .replace(/\s{2,}/g, " ")
      .trim(),
  };
};

function App() {
  return (
    <html lang="en">
      <head>
        <Meta />
        <Links />
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-76JHLBKEGQ"
        ></script>
        <script
          dangerouslySetInnerHTML={{
            __html: `window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-76JHLBKEGQ');`,
          }}
        />
        <script
          src="//code.tidio.co/rjfro0z8yeefeyuubc9rsuxch6wx0cpz.js"
          async
        ></script>
        <script
          src="https://analytics.ahrefs.com/analytics.js"
          data-key="Y5kP8LKovu1RweTJIFFguQ"
          async
        ></script>
      </head>
      <body>
        <Outlet />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  captureRemixErrorBoundaryError(error);

  // Check if the error is related to network connectivity
  const isNetworkError =
    error instanceof Error &&
    (error.name === "TypeError" ||
      error.message.toLowerCase().includes("failed to fetch") ||
      error.message.toLowerCase().includes("network") ||
      error.message.toLowerCase().includes("offline") ||
      error.message.toLowerCase().includes("connection") ||
      error.message.toLowerCase().includes("timeout"));

  return (
    <html lang="en" className="h-full">
      <head>
        <title>Oh no!</title>
        <Meta />
        <Links />
      </head>
      <body className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700">
        <div className="flex flex-col items-center justify-center min-h-screen p-4 text-white">
          <div className="max-w-md text-center">
            <h1 className="text-4xl font-bold mb-4">
              {isNetworkError
                ? "Connection Error"
                : isRouteErrorResponse(error)
                ? `${error.status} ${error.statusText}`
                : "Application Error"}
            </h1>
            <p className="mb-8">
              {isNetworkError
                ? "Unable to connect. Please check your internet connection and try again."
                : isRouteErrorResponse(error)
                ? error.data
                : "Sorry, something went wrong. Please try again later."}
            </p>
            <a
              href="/"
              className="inline-block px-6 py-3 rounded-lg bg-white text-blue-600 font-semibold hover:bg-blue-50 transition-colors"
            >
              Return to Home
            </a>
          </div>
        </div>
        <Scripts />
      </body>
    </html>
  );
}

export default ClerkApp(App);
